import { Link, useLocation, useNavigate } from 'react-router-dom';
import ArrowLeft from '../../Assets/Arrow left.svg';
import { useContext, useEffect, useState } from 'react';
import { AddSizeChartContext } from '../../Context/AddSizeChartContext';
import {
    defaultTShirtFormObject,
    defaultTrousersFormObject,
    defaultWomenDressFormObject,
    defaultCoatsFormObject,
    defaultShoeFormObject,
    defaultHeelFormObject,
    defaultClothingSetFormObject,
} from './SizeChartFormsData';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSizeCharts, setModal } from '../../Features/inventoryManagementSlice';
import ModalContainer from '../../Components/ModalContainer/ModalContainer';
import RowRemoveModalContainer from './Modals/RowRemoveModal/RowRemoveModalContainer';
import RearrangeInputsModal from './Modals/RearrangeInputsModal';
import SizesForm from './Form/SizesForm';
import { showToast } from '../../Components/Toaster/Toaster';
import { addSizeChart } from '../../lib/utils/apiCalls';
import AddColumn from './Modals/AddColumn';
import ColumnRemoveModalContainer from './Modals/ColumnRemoveModal/ColumnRemoveModalContainer';
import LoadingTransparent from '../../Components/Loading/LoadingTransparent';

function getDefaultFormByCategory(category) {
    switch (category) {
        case 'T-Shirt, Shirt, Top':
            return { ...defaultTShirtFormObject, id: uuidv4() };
        case 'Trousers, Phants, Jeans':
            return { ...defaultTrousersFormObject, id: uuidv4() };
        case 'Women Dresses':
            return { ...defaultWomenDressFormObject, id: uuidv4() };
        case 'Coat & Jacket':
            return { ...defaultCoatsFormObject, id: uuidv4() };
        case 'Shoe & Sandals':
            return { ...defaultShoeFormObject, id: uuidv4() };
        case 'High Heels':
            return { ...defaultHeelFormObject, id: uuidv4() };
        case 'Clothing Set':
            return { ...defaultClothingSetFormObject, id: uuidv4() };
        default:
            return null;
    }
}

function AddSizeChart() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const defaultFormErrors = {
        name: '',
        sizes: '',
    };

    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    const { activeModal: type, displayModal: open } = useSelector((state) => state.inventoryManagement.modal);

    const {
        selectedCategory,
        setSelectedCategory,
        sizeChartName,
        setSizeChartName,
        sizeChartFormData,
        setSizeChartFormData,
    } = useContext(AddSizeChartContext);

    function handleCancel() {
        const newRow = getDefaultFormByCategory(selectedCategory);
        setSizeChartFormData([newRow]);
    }

    function addNewRow() {
        const newSection = getDefaultFormByCategory(selectedCategory);
        if (newSection) {
            setSizeChartFormData((prev) => [...prev, newSection]);
        }
    }

    function addNewColumn() {
        dispatch(setModal('add-column'));
    }

    function handleRemoveRow() {
        dispatch(setModal('remove-row'));
    }

    function handleRemoveColumn() {
        dispatch(setModal('remove-column'));
    }

    useEffect(() => {
        const storedCategory = localStorage.getItem('selectedCategory');
        setSelectedCategory(storedCategory);
        const formObject = getDefaultFormByCategory(storedCategory);
        setSizeChartFormData(formObject ? [formObject] : []);
    }, []);

    async function handleSave() {
        const validationErrors = {};

        if (!sizeChartName) {
            validationErrors.name = 'Name is required';
        }

        if (sizeChartFormData.length === 0) {
            showToast('Add at least one size before saving.', 'warning');
            return;
        }

        const visibleKeys = Object.keys(sizeChartFormData[0]).filter(
            (key) => key !== 'id' && sizeChartFormData.some((row) => row[key] !== undefined)
        );

        const hasEmptyField = sizeChartFormData.some((row) =>
            visibleKeys.some((key) => {
                const value = row[key];
                if (!value) return false;
                if (typeof value === 'object' && 'cm' in value && 'in' in value) {
                    return !value.cm || !value.in;
                }
                if (typeof value === 'string') {
                    return value.trim() === '';
                }
                return false;
            })
        );

        if (hasEmptyField) {
            validationErrors.sizes = 'Empty field not allowed';
        }

        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        setLoading(true);
        setFormErrors(defaultFormErrors);

        const filteredFormData = sizeChartFormData.map((row) => {
            const filtered = {};

            visibleKeys.forEach((key) => {
                filtered[key] = row[key];
            });

            if (!filtered.id) {
                filtered.id = uuidv4();
            }

            return filtered;
        });

        let transformedSizes = filteredFormData;

        if (selectedCategory === 'Clothing Set') {
            transformedSizes = filteredFormData.map((row) => {
                if (row.outseam) {
                    const { outseam, ...rest } = row;
                    return { ...rest, outSeam: outseam };
                }
                return row;
            });
        }

        const callBody = {
            chartName: sizeChartName,
            chartType: selectedCategory,
            sizes: transformedSizes,
        };

        try {
            const response = await addSizeChart(callBody);
            if (response.status === 200) {
                showToast('Size chart added.', 'success');
                dispatch(fetchSizeCharts());
                navigate(`${location.pathname.split('/').slice(0, -1).join('/')}`);
            } else {
                showToast('Failed to add size chart.', 'error');
            }
        } catch (err) {
            showToast('Failed to add size chart.', 'error');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="size-full overflow-y-scroll pb-40 outline-none">
            <div className="select-none flex items-center justify-between gap-6 relative m-[20px_19px_24px_20px]">
                <div className="w-fit flex items-center">
                    <Link to=".." className="p-[10px] flex items-center justify-center">
                        <img className="size-6 text-[#666666]" src={ArrowLeft} alt="" />
                    </Link>

                    <h1 className="p-[6px] text-[#121212] font-bold leading-8 text-[24px]">
                        {selectedCategory === 'Trousers, Phants, Jeans'
                            ? 'Trousers, Pants, Jeans'
                            : selectedCategory}
                    </h1>
                </div>
            </div>

            <div className="flex flex-col gap-4 mt-4 ml-16 mr-5">
                <div className="flex flex-col gap-3">
                    <div className="w-3/5 flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <label htmlFor="sizeChartName" className="text-base text-[#121212] font-medium">
                                Name your Size Chart
                            </label>

                            {formErrors.name && (
                                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                    {formErrors.name}
                                </p>
                            )}
                        </div>

                        <input
                            className="text-base font-normal px-3 py-4 rounded-[9px] bg-[#F5F5F5] border border-[#EAEAEA] outline-none focus:border-primaryColor placeholder:text-[#666666] text-[#121212]"
                            type="text"
                            id="sizeChartName"
                            name="sizeChartName"
                            placeholder="Enter the name here........"
                            value={sizeChartName}
                            onChange={(e) => setSizeChartName(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <p className="text-base text-[#121212] font-medium">
                            Enter accurate sizes into the chart and save.
                        </p>

                        <div className="max-w-full overflow-x-scroll overscroll-y-none flex flex-col gap-2">
                            <SizesForm />
                        </div>

                        {formErrors.sizes && (
                            <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                {formErrors.sizes}
                            </p>
                        )}

                        <div className="w-3/5 flex justify-between gap-2 text-base font-medium text-[#1055EB]">
                            <div className="flex items-center gap-3">
                                <button className="outline-none" onClick={addNewRow}>
                                    Add Row
                                </button>
                                <button className="outline-none" onClick={addNewColumn}>
                                    Add Column
                                </button>
                            </div>

                            <div className="flex items-center gap-3">
                                <button className="outline-none" onClick={handleRemoveRow}>
                                    Remove Row
                                </button>
                                <button className="outline-none" onClick={handleRemoveColumn}>
                                    Remove Column
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="full flex items-center gap-[10px] justify-end">
                    <button
                        onClick={handleCancel}
                        className="px-5 py-[10px] rounded outline-none text-[#757575] text-base font-semibold"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSave}
                        className="px-5 py-[10px] rounded outline-none text-white bg-primaryColor hover:bg-primaryAccent text-base font-semibold"
                    >
                        Save
                    </button>
                </div>
            </div>

            {open && (
                <ModalContainer>
                    {type === 'remove-row' && <RowRemoveModalContainer />}
                    {type === 'remove-column' && <ColumnRemoveModalContainer />}
                    {type === 'rearrange-row' && <RearrangeInputsModal />}
                    {type === 'add-column' && <AddColumn />}
                </ModalContainer>
            )}

            {loading && <LoadingTransparent />}
        </div>
    );
}

export default AddSizeChart;
